import { Field, FieldArray, Form, Formik } from 'formik';
import React from 'react';
import { DeleteIcon, AddLightMode as AddIcon } from 'src/assets/images';
import {
  ApplicationAction,
  ApplicationActionType,
  HeadingLayout,
  HeadingOne,
  HeadingTwo,
  InputField,
  useWizardEffect,
} from 'src/features/common';
import { Accordion } from 'src/features/common/components/accordion/accordion';
import { Toast, notify } from 'src/features/common/components/toast/toast';
import { getApplicationAPIFromApplication } from 'src/features/common/helpers/dto-mappers/salesforce/get-application-api-from-application';
import { CardMachine } from 'src/features/common/types/application/card-machine';
import { PaymentStatus } from 'src/features/common/types/application/payment';
import { ConfirmModal } from 'src/features/device-activation/components';
import {
  createDeviceActivationActions,
  groupAndFormatSerialNumbers,
} from 'src/features/device-activation/helpers/utils';
import { useApplication } from 'src/hooks/useApplication';
import { Environment } from 'src/Environment';
import { useAsyncDispatch } from 'src/features/order/hooks/use-async-dispatch';

type FormFields = {
  cardMachines: CardMachine[];
};

export function AssignName() {
  const { application, dispatch, catalogue, salesforceService, deviceActivationTealiumService } =
    useApplication();
  const dispatchAsync = useAsyncDispatch(application, dispatch);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [barcodeToBeDeleted, setBarcodeToBeDeleted] = React.useState('');

  const isDpoEnabled = React.useMemo((): boolean => !Environment.current.disabledFeatures.dpo, []);

  const getInitialValues = (): FormFields => {
    return {
      cardMachines: application.cardMachines || [],
    };
  };

  const [isOpenIndex, setIsOpenIndex] = React.useState(-1);

  function isAnyFormErrors(values: FormFields) {
    return (
      (values.cardMachines || []).filter((machine) => !machine.name?.trim()).length ||
      application.cardMachines?.length === 0
    );
  }

  const { setNextButtonEnabled, setOnNextEventHandler, setLoadingOverlayVisible } = useWizardEffect(
    () => ({
      nextButtonLabel: 'Next',
      backButtonVisible: true,
      nextButtonEnabled: !isAnyFormErrors(getInitialValues()),
    }),
    [application],
  );

  async function submit(values: FormFields) {
    const formattedSerialNumbers = groupAndFormatSerialNumbers(values.cardMachines);
    const deviceActivationActions = createDeviceActivationActions(
      formattedSerialNumbers,
      catalogue,
      isDpoEnabled,
    );

    deviceActivationTealiumService?.onAssignNameEnd(application.referenceNumber);

    setLoadingOverlayVisible(true);

    const updatedApplication = await dispatchAsync({
      type: ApplicationActionType.MULTIPLE_ACTIONS,
      actions: deviceActivationActions,
    });

    const applicationNew = getApplicationAPIFromApplication(updatedApplication, catalogue);

    let actions: ApplicationAction[] = [];

    if (applicationNew.deliveries?.length) {
      const savedApplicationResponse = await salesforceService.saveApplication(
        applicationNew,
        true,
      );

      actions = [
        {
          type: ApplicationActionType.UPDATE_REFERENCE_NUMBER,
          referenceNumber: savedApplicationResponse?.referenceNumber,
        },
        {
          type: ApplicationActionType.UPDATE_PAYMENT_STATUS,
          paymentStatus: PaymentStatus.PAYMENT_SUCCESSFUL,
        },
      ];
    }

    setLoadingOverlayVisible(false);

    dispatch({
      type: ApplicationActionType.MULTIPLE_ACTIONS,
      actions: [...actions, { type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP }],
    });
  }

  function validate(values: FormFields) {
    setNextButtonEnabled(!isAnyFormErrors(values));
  }

  function onFinishTyping(values: FormFields) {
    dispatch({
      type: ApplicationActionType.UPDATE_CARD_MACHINE,
      cardMachines: values.cardMachines,
    });
  }

  function getMachineTitle(cardMachine: CardMachine): string | undefined {
    const machine = application.cardMachines?.filter(
      (mac) => mac.barcode === cardMachine.barcode,
    )?.[0];

    return machine?.name || machine?.barcode;
  }

  React.useEffect(() => {
    deviceActivationTealiumService?.onAssignNameStart(application.referenceNumber);
  }, []);

  return (
    <section className="flex flex-col items-center">
      <HeadingLayout
        renderMainHeading={() => <HeadingOne>Name your card machine</HeadingOne>}
        renderSubHeading={() => (
          <>
            <HeadingTwo>Assign a friendly name (nickname) to your card machine(s)</HeadingTwo>
          </>
        )}
      />
      <ConfirmModal
        onConfirm={() => {
          deviceActivationTealiumService?.onConfirmationModalEnd(
            'cancel',
            application.referenceNumber,
          );
          setBarcodeToBeDeleted('');
          setShowConfirmModal(false);
        }}
        onClose={() => {
          setBarcodeToBeDeleted('');
          setShowConfirmModal(false);
        }}
        heading="Are you sure?"
        visible={showConfirmModal}
        onVisible={() => {
          deviceActivationTealiumService?.onConfirmationModalStart(application.referenceNumber);
        }}
        subtext="You are about the delete a card machine"
        confirmButtonLabel="Cancel"
        cancelButtonLabel="Delete card machine"
        onCancel={() => {
          deviceActivationTealiumService?.onConfirmationModalEnd(
            'delete card machine',
            application.referenceNumber,
          );
          const machines = (application.cardMachines || []).filter(
            (m) => m.barcode !== barcodeToBeDeleted,
          );
          dispatch({
            type: ApplicationActionType.UPDATE_CARD_MACHINE,
            cardMachines: machines,
          });
          setShowConfirmModal(false);
          notify({
            children: 'Card machine deleted',
            autoClose: 5000,
          });
        }}
      />
      <div className="md:min-w-[600px] md:px-0 px-4 min-w-full mt-[52px]">
        <Toast />
        <Formik
          innerRef={(formik) => formik && setOnNextEventHandler(formik.submitForm)}
          validate={validate}
          onSubmit={submit}
          validateOnChange={true}
          validateOnBlur
          validateOnMount
          enableReinitialize={true}
          initialValues={getInitialValues()}
        >
          {({ values }) => {
            return (
              <Form>
                <FieldArray
                  name="cardMachines"
                  render={() => (
                    <div className="flex flex-col gap-5">
                      {values.cardMachines.map((machine, index) => (
                        <Accordion
                          key={index}
                          className="rounded-20 [&:h1]:uppercase border-2 px-10 py-6 border-[#F2F4F7]"
                          title={`${index + 1}. ${getMachineTitle(machine) || ''}`}
                          onClick={() => setIsOpenIndex(index === isOpenIndex ? -1 : index)}
                          rightIcons={
                            <img
                              className="cursor-pointer pointer-events-auto"
                              onClick={() => {
                                setBarcodeToBeDeleted(machine.barcode);
                                setShowConfirmModal(true);
                                deviceActivationTealiumService?.onDeleteDevice(
                                  application.referenceNumber,
                                );
                              }}
                              src={DeleteIcon}
                              alt={'delete icon'}
                            />
                          }
                        >
                          <Field
                            name={`cardMachines.${index}.name`}
                            type="text"
                            onBlur={() => onFinishTyping(values)}
                            id={`cardMachines.${index}.name`}
                            subtext="Example: Thabo's Kwika or Kwika Sandton Store"
                            placeholder="Card machine name"
                            component={InputField}
                            fieldType="standard"
                            className="light"
                            automationid="Revamp_Your_Details_First_Name"
                          />
                        </Accordion>
                      ))}
                    </div>
                  )}
                />
              </Form>
            );
          }}
        </Formik>
        <section className="flex items-center justify-center gap-3 mt-5">
          <img
            onClick={() => {
              deviceActivationTealiumService?.onAddNewDevice(application.referenceNumber);
              dispatch({ type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP });
            }}
            className="cursor-pointer"
            src={AddIcon}
            alt="+"
          />
          <p
            onClick={() => dispatch({ type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP })}
            className="text-base font-medium cursor-pointer"
          >
            Add another card machine
          </p>
        </section>
      </div>
    </section>
  );
}
